import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, NgZone } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { Observable, concat, merge, of, race, timer } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  startWith,
  switchMap,
  take,
} from 'rxjs/operators';

/**
 * Whether the current page has finished growing to its loaded height.
 *
 * The Ask AI panel tucks its bottom edge under however much of the page footer
 * is on screen when it opens. Right after a refresh or navigation a portal page
 * renders short — the footer sits high — and then its data lands and pushes the
 * footer below the fold. Opening the panel inside that window left it short of
 * the viewport bottom. The entry point waits for this signal instead, so the
 * panel can only be opened once the page has stopped resizing.
 *
 * "Settled" means the page body has not changed size for [QUIET_MS]. It resets
 * on every navigation, and [MAX_WAIT_MS] caps the wait so a page that keeps
 * resizing (a live widget, a carousel) can never hide the entry point for good.
 */
@Injectable({ providedIn: 'root' })
export class OrbiPageSettledService {
  static readonly QUIET_MS = 500;
  static readonly MAX_WAIT_MS = 5000;

  /** Emits each time the page body changes size (in the Angular zone). */
  private readonly bodyResizes$ = new Observable<void>((subscriber) => {
    const body = this.document.body;
    if (typeof ResizeObserver === 'undefined' || !body) {
      return undefined;
    }
    const observer = new ResizeObserver(() =>
      this.zone.run(() => subscriber.next())
    );
    observer.observe(body);
    return () => observer.disconnect();
  });

  /** False from app start and each navigation until the page stops resizing. */
  readonly settled$: Observable<boolean> = merge(
    of(undefined),
    this.router.events.pipe(filter((event) => event instanceof NavigationStart))
  ).pipe(
    switchMap(() =>
      concat(
        of(false),
        race(
          this.bodyResizes$.pipe(
            startWith(undefined),
            debounceTime(OrbiPageSettledService.QUIET_MS),
            take(1)
          ),
          timer(OrbiPageSettledService.MAX_WAIT_MS)
        ).pipe(map(() => true))
      )
    ),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  constructor(
    private readonly router: Router,
    private readonly zone: NgZone,
    @Inject(DOCUMENT) private readonly document: Document
  ) {}
}
